/**
 * LivePerson script provided by Bish Labib <blabib@australiansuper.com> from AusSuper.
 * LivePerson contact is Sandeep Karuppuswamy <skaruppuswamy@liveperson.com>.
 *
 * Generated from LivePerson account/dashboard.
 * Ref: https://knowledge.liveperson.com/getting-started-add-the-liveperson-tag-to-your-website.html
 */

import Cta from 'core/components/Cta';
import { useAppContext } from 'core/hooks';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import FormBuilder from 'pods/forms/FormBuilder';
import { Caption } from 'core/styles/Typography';
import { LIVE_PERSON_MODAL_FORM, MODAL_ARIA_NAMES } from 'core/constants';
import LoadingSpinner from 'core/components/LoadingSpinner';
import IFormConfig from 'pods/forms/FormBuilder.d';
import RichText from 'core/components/RichText';
import { getMessageByCode } from 'core/utils/sitecore-utils';
import { CO_BROWSE_ERRORS } from 'core/constants/errors';

import * as S from '../LivePerson.style';
import { IFields } from '../LivePerson.sitecore';

interface IForm {
	serviceNumber: string;
}

const LPModalForm = ({
	fields,
	onSubmit,
	loading,
	serviceNumber,
	isServiceIdError,
}: {
	fields: IFields;
	onSubmit: (serviceId: string) => void;
	loading: boolean;
	serviceNumber: string;
	isServiceIdError: boolean;
}) => {
	const { handleLivePersonModal } = useAppContext();

	const ER65 = getMessageByCode(
		CO_BROWSE_ERRORS.SERVICE_CODE_INVALID,
		fields?.errorMessages,
		'errorMessage',
	);

	const formConfig: IFormConfig = {
		version: 0,
		id: 'service-number',
		fields: [
			{
				id: 'service-number',
				path: 'serviceNumber',
				type: 'text',
				required: true,
				disabled: false,
				readonly: false,
				requiredMessage: LIVE_PERSON_MODAL_FORM.REQUIRED,
				errorSummaryLabel: LIVE_PERSON_MODAL_FORM.ERROR_MESSAGE,
				hasCloseIcon: true,
				enableButtonWithin: true,
				data: {
					ctrlHolderFullWidth: true,
					placeholder: fields?.serviceNumberLabel?.value,
					endAdornment: null,
					startAdornment: null,
					appendMsg: ReactDOMServer.renderToString(
						<Caption textAlign="left">
							{fields?.liveAssistNumberInfo?.value}
						</Caption>,
					),
					fullWidth: true,
				},
				validators: [
					{
						type: 'lpServiceId',
						message: ER65,
					},
					{
						type: 'lpServiceIdRejected',
						options: isServiceIdError,
						message: ER65,
					},
				],
			},
		],
		initialValues: {
			serviceNumber: serviceNumber,
		},
	};

	return (
		<>
			<div id={MODAL_ARIA_NAMES.DESCRIPTION}>
				<RichText
					content={
						fields?.liveAssistDescription?.value ??
						LIVE_PERSON_MODAL_FORM.DESCRIPTION
					}
				/>
			</div>
			{loading ? (
				<S.LpLoadingContainer>
					<LoadingSpinner size={30} />
					<S.LpLoadingTextContainer>
						{fields?.loadingMessage?.value ??
							LIVE_PERSON_MODAL_FORM.LOADING_TEXT}
					</S.LpLoadingTextContainer>
				</S.LpLoadingContainer>
			) : (
				<>
					<FormBuilder
						config={formConfig}
						renderButtons={() => (
							<>
								<S.Hr />
								<S.CtaContainer>
									<Cta
										ctaStyle="primary"
										ctaType="button"
										buttonType="submit"
										data-target-id="live-person-submit-label"
										size="medium"
									>
										{fields?.startLabel?.value}
									</Cta>
									<Cta
										ctaStyle="plain"
										ctaType="button"
										onClick={() => {
											handleLivePersonModal(false);
										}}
										data-target-id="live-person-cancel-label"
										size="medium"
									>
										{fields?.cancelButtonLabel?.value}
									</Cta>
								</S.CtaContainer>
							</>
						)}
						onSubmit={(values: IForm) => {
							onSubmit(values.serviceNumber);
						}}
					/>
				</>
			)}
		</>
	);
};

export default LPModalForm;
